<script setup lang="ts">
import { computed, ref } from 'vue';
import {
    BChip, BIcon, BMenu, BList, BListItem, BListItemTitle,
} from '@/design-system';

type ItemType = {
    value: string;
    label: string;
};

type PropsType = {
    modelValue: string;
    items: ItemType[];
    icon?: string;
};

const props = defineProps<PropsType>();

const emit = defineEmits<{
    'update:modelValue': [value: string];
}>();

const isMenuOpen = ref(false);

const selectedItem = computed(() => props.items.find(item => item.value === props.modelValue));

const displayLabel = computed(() => selectedItem.value?.label ?? '');

const handleSelect = (value: string) => {
    emit('update:modelValue', value);
    isMenuOpen.value = false;
};

const isItemSelected = (itemValue: string) => itemValue === props.modelValue;
</script>

<template>
    <BMenu
        v-model="isMenuOpen"
        location="top"
    >
        <template #activator="{ props: menuProps }">
            <BChip
                v-bind="menuProps"
                variant="flat"
                class="filter-chip-select"
            >
                <BIcon
                    v-if="icon"
                    :icon="icon"
                    class="mr-1"
                    size="small"
                />
                <span>{{ displayLabel }}</span>
                <BIcon
                    icon="mdi-chevron-up"
                    class="ml-1"
                    size="small"
                />
            </BChip>
        </template>

        <template #list-items>
            <BList class="filter-chip-select__list">
                <BListItem
                    v-for="item in items"
                    :key="item.value"
                    :class="{ 'filter-chip-select__list-item--selected': isItemSelected(item.value) }"
                    @click="handleSelect(item.value)"
                >
                    <BListItemTitle>{{ item.label }}</BListItemTitle>
                </BListItem>
            </BList>
        </template>
    </BMenu>
</template>

<style scoped lang="scss">
.filter-chip-select {
    cursor: pointer;
}

.filter-chip-select__list {
    min-width: 120px;
}

.filter-chip-select__list-item--selected {
    background-color: rgba(var(--v-theme-primary), 0.1);
}
</style>
